import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL, absoluteUrl, absoluteImage, DEFAULT_OG_IMAGE, COMPANY } from "@/lib/seo";

/**
 * Central per-page SEO: title, meta description, canonical, hreflang,
 * Open Graph, Twitter cards, and JSON-LD structured data.
 */
export default function Seo({
  title,
  description,
  path = "/",
  canonical = true,
  image,
  type = "website",
  jsonLd = [],
  noindex = false,
}) {
  const canonicalUrl = absoluteUrl(path);
  const ogImage = image ? absoluteImage(image) : DEFAULT_OG_IMAGE;
  const fullTitle = title
    ? `${title} | ${COMPANY.name}`
    : `${COMPANY.name} | Premium Indian Millet, Bajra & Grain Exporter`;
  const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {canonical && <link rel="canonical" href={canonicalUrl} />}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />

      {/* hreflang — English + default */}
      {!noindex && <link rel="alternate" hrefLang="en" href={canonicalUrl} />}
      {!noindex && <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={COMPANY.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={`${COMPANY.name} — ${title || "Indian agricultural exports"}`} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={`${COMPANY.name} — ${title || "Indian agricultural exports"}`} />

      {schemas.filter(Boolean).map((schema, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export { SITE_URL };
